// Browser-side ad pixels for the app (app.clipbait.ai). Loaded on demand —
// only to report a new signup — rather than on every page.
//
// Why: signups that finish in the app (Google sign-in, the app's own email
// login) never touch clipbait.ai, so TikTok/Meta only heard about them from
// the server. Ad platforms want the browser event too to unlock
// CompleteRegistration for optimization. We fire with event_id
// "signup_<userId>" — the same id the server uses (services/signupEvents.js)
// — so each platform dedupes the pair into one conversion.
//
// Same pixel ids as the marketing site (clipbait-marketing-site/lib).
const TIKTOK_PIXEL_ID = "D8NM8PJC77UCVEHVNM40";
const META_PIXEL_ID = "995370606591747";

function loadTikTok() {
  if (window.ttq) return window.ttq;
  /* eslint-disable */
  !function (w, d, t) {
    w.TiktokAnalyticsObject = t; var ttq = w[t] = w[t] || [];
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
    ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t) { for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]); return e; };
    ttq.load = function (e, n) {
      var r = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {}, ttq._i[e] = [], ttq._i[e]._u = r, ttq._t = ttq._t || {}, ttq._t[e] = +new Date, ttq._o = ttq._o || {}, ttq._o[e] = n || {};
      var o = d.createElement("script"); o.type = "text/javascript", o.async = !0, o.src = r + "?sdkid=" + e + "&lib=" + t;
      var a = d.getElementsByTagName("script")[0]; a.parentNode.insertBefore(o, a);
    };
    ttq.load(TIKTOK_PIXEL_ID);
  }(window, document, "ttq");
  /* eslint-enable */
  return window.ttq;
}

function loadMeta() {
  if (window.fbq) return window.fbq;
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  }(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", META_PIXEL_ID);
  return window.fbq;
}

async function sha256(value) {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Report a brand-new account from the browser (deduped with the server). */
export async function trackSignupPixels(user) {
  if (!user?._id || typeof window === "undefined") return;
  const eventId = `signup_${user._id}`;
  try {
    const ttq = loadTikTok();
    // TikTok's spec: identify with SHA-256 hashed email + external_id first.
    if (window.crypto?.subtle) {
      const ids = { external_id: await sha256(String(user._id)) };
      if (user.email) ids.email = await sha256(String(user.email).trim().toLowerCase());
      ttq.identify(ids);
    }
    ttq.track(
      "CompleteRegistration",
      { contents: [{ content_id: "signup", content_type: "product", content_name: "Clipbait account" }] },
      { event_id: eventId }
    );
  } catch (_) { /* ad pixels must never break sign-in */ }
  try {
    loadMeta()("track", "CompleteRegistration", { content_name: "signup" }, { eventID: eventId });
  } catch (_) { /* ignore */ }
}

// Meta's browser ids (_fbp / _fbc cookies), sent with checkout so the server
// StartTrial matches this browser.
export function metaCookies() {
  const get = (k) => (document.cookie.match(new RegExp(`(?:^|; )${k}=([^;]*)`)) || [])[1];
  try { return { fbp: get("_fbp"), fbc: get("_fbc") }; } catch (_) { return {}; }
}

/** Opened the $1 trial checkout. Also sets the _fbp cookie before they pay. */
export function trackTrialCheckoutPixel() {
  try { loadMeta()("track", "InitiateCheckout", { value: 1, currency: "USD", content_name: "trial" }); } catch (_) { /* ignore */ }
}

/**
 * Started the $1 trial. event_id "trial_<userId>" is the same one the server
 * sends (services/checkoutFunnel.js trackTrialStart), so Meta and TikTok each
 * count it once.
 */
export function trackTrialStartPixel(user, { predictedLtv = 24 } = {}) {
  if (!user?._id) return;
  try {
    loadTikTok().track(
      "StartTrial",
      { value: 1, currency: "USD", contents: [{ content_id: "trial", content_type: "product", content_name: "Clipbait $1 trial" }] },
      { event_id: `trial_${user._id}` }
    );
  } catch (_) { /* ignore */ }
  try {
    loadMeta()("track", "StartTrial", { value: 1, currency: "USD", predicted_ltv: predictedLtv }, { eventID: `trial_${user._id}` });
  } catch (_) { /* ignore */ }
}
